export const DEFAULT_SLIDE_IMAGE_TILT = 'RIGHT' as const;

export type SlideImageTiltValue = 'LEFT' | 'RIGHT' | 'NONE';

const SLIDE_IMAGE_TILTS: SlideImageTiltValue[] = ['LEFT', 'RIGHT', 'NONE'];

/** Auto-assigned Admin title, e.g. "Slide 1". Not Stage copy. */
const DEFAULT_SLIDE_TITLE_RE = /^slide\s+\d+$/i;

export function isSlideImageTilt(value: unknown): value is SlideImageTiltValue {
  return (
    typeof value === 'string' &&
    SLIDE_IMAGE_TILTS.includes(value as SlideImageTiltValue)
  );
}

/** Missing values match today's Stage animation: image rotates to +8deg (right). */
export function normalizeSlideImageTilt(value: unknown): SlideImageTiltValue {
  return isSlideImageTilt(value) ? value : DEFAULT_SLIDE_IMAGE_TILT;
}

function asSlideString(value: unknown): string {
  return typeof value === 'string' ? value : '';
}

function stripInvisibleAndEntities(value: string): string {
  return value
    .replace(/[\u00AD\u200B-\u200D\u2060\uFEFF]/g, '')
    .replace(/&nbsp;|&#160;|&#x0*a0;/gi, ' ')
    .replace(/&[a-z0-9]+;|&#\d+;|&#x[0-9a-f]+;/gi, ' ');
}

function decodeBasicEntities(value: string): string {
  return value
    .replace(/&amp;/gi, '&')
    .replace(/&lt;/gi, '<')
    .replace(/&gt;/gi, '>')
    .replace(/&quot;/gi, '"')
    .replace(/&#39;|&apos;/gi, "'");
}

function stripHtmlTags(value: string): string {
  return value.replace(/<[^>]*>/g, ' ');
}

function collapseWs(value: string): string {
  return value.replace(/\s+/g, ' ').trim();
}

function stripMarkdownChrome(value: string): string {
  return value
    .replace(/^#{1,6}\s+/gm, '')
    .replace(/^\s*>+\s?/gm, '')
    .replace(/[*_~`]+/g, '')
    .replace(/!\[[^\]]*]/g, '')
    .replace(/\[[^\]]*]/g, '')
    .replace(/[()|\\]/g, ' ');
}

/**
 * Visible-ish text left after removing HTML, entities, zero-width chars,
 * and markdown punctuation. Used so image-only slides can go full-bleed.
 */
export function slideMarkdownPlainText(content: unknown): string {
  const raw = asSlideString(content);
  if (!raw) {
    return '';
  }
  const cleaned = stripHtmlTags(
    decodeBasicEntities(stripInvisibleAndEntities(raw)),
  );
  return collapseWs(stripMarkdownChrome(cleaned));
}

function isDefaultSlideTitle(value: string): boolean {
  return DEFAULT_SLIDE_TITLE_RE.test(value);
}

function sameSlideCopy(a: string, b: string): boolean {
  return (
    a.length > 0 &&
    a.localeCompare(b, undefined, { sensitivity: 'accent' }) === 0
  );
}

/**
 * Empty, HTML-empty, punctuation-only, default "Slide N", or copy that only
 * repeats the Admin title is treated as no Stage markdown.
 */
export function hasMeaningfulSlideMarkdown(
  content: string | null | undefined,
  title?: string | null,
): boolean {
  const plain = slideMarkdownPlainText(content);
  if (!plain) {
    return false;
  }
  if (isDefaultSlideTitle(plain)) {
    return false;
  }
  const titlePlain = slideMarkdownPlainText(title);
  if (titlePlain && sameSlideCopy(plain, titlePlain)) {
    return false;
  }
  // Punctuation-only leftovers (---, …, quotes) are not Stage copy.
  return /[\p{L}\p{N}]/u.test(plain);
}

export function isImageOnlySlide({
  content,
  imageUrl,
  title,
}: {
  content?: string | null;
  imageUrl?: string | null;
  title?: string | null;
}): boolean {
  return Boolean(imageUrl) && !hasMeaningfulSlideMarkdown(content, title);
}

export type SlideImageTiltStyles = {
  enterFlat: string;
  tiltFullBleedLeft: string;
  tiltFullBleedRight: string;
  tiltImageLeft: string;
  tiltImageRight: string;
  tiltContentLeft: string;
  tiltContentRight: string;
};

/** Stage image-only tilt angle. Keep in sync with `tiltFullBleed*` keyframes. */
export const SLIDE_IMAGE_ONLY_TILT_DEGREES = 8;

/** StageChrome sidebar `w-[21vw]`. */
export const STAGE_SIDEBAR_WIDTH_RATIO = 0.21;
/** CENTER `mx-[2vw]`. */
export const STAGE_CENTER_MARGIN_X_RATIO = 0.02;
/** CENTER engagement `px-[4vw]`. */
export const STAGE_CENTER_PAD_X_RATIO = 0.04;
/** CENTER `my-[2vh]` (both sides → 4vh, paired with `h-[calc(100%-4vh)]`). */
export const STAGE_CENTER_MARGIN_Y_RATIO = 0.02;
/** CENTER engagement `pt-[7vh]`. */
export const STAGE_CENTER_PAD_TOP_RATIO = 0.07;
/** CENTER engagement `pb-[3vh]`. */
export const STAGE_CENTER_PAD_BOTTOM_RATIO = 0.03;
/**
 * Content-to-sidebar gap is mx 2vw + px 4vw = 6vw, already more than 3vw.
 * Do not add another 3% inset on the slide.
 */
export const STAGE_CONTENT_TO_SIDEBAR_GAP_RATIO =
  STAGE_CENTER_MARGIN_X_RATIO + STAGE_CENTER_PAD_X_RATIO;

/**
 * Uniform scale so a square’s AABB at ±8° stays inside the safe area.
 * Applied on the fit *wrapper* for EVERY tilt, including NONE, so the card
 * never shrinks when it starts rotating. Tilt keyframes only rotate.
 */
export const SLIDE_IMAGE_ONLY_TILT_FIT_SCALE =
  1 /
  (Math.abs(Math.cos((SLIDE_IMAGE_ONLY_TILT_DEGREES * Math.PI) / 180)) +
    Math.abs(Math.sin((SLIDE_IMAGE_ONLY_TILT_DEGREES * Math.PI) / 180)));

export function imageOnlySlideTiltFitScale(
  tilt: SlideImageTiltValue = 'RIGHT',
): number {
  void tilt;
  return SLIDE_IMAGE_ONLY_TILT_FIT_SCALE;
}

/** Engagement content box inside StageChrome CENTER (after the height fix). */
export function computeStageCenterContentBox(
  viewportWidth: number,
  viewportHeight: number,
): { width: number; height: number } {
  const width =
    viewportWidth *
    (1 -
      STAGE_SIDEBAR_WIDTH_RATIO -
      STAGE_CENTER_MARGIN_X_RATIO * 2 -
      STAGE_CENTER_PAD_X_RATIO * 2);
  const height =
    viewportHeight *
    (1 -
      STAGE_CENTER_MARGIN_Y_RATIO * 2 -
      STAGE_CENTER_PAD_TOP_RATIO -
      STAGE_CENTER_PAD_BOTTOM_RATIO);
  return { width, height };
}

/** Side length of the square card: contain in the safe area, then ~0.885. */
export function computeImageOnlyFitSquare(box: {
  width: number;
  height: number;
}): number {
  return Math.min(box.width, box.height) * SLIDE_IMAGE_ONLY_TILT_FIT_SCALE;
}

/** object-contain into the box, including upscale so small assets fill Stage. */
export function containImageInBox({
  imageWidth,
  imageHeight,
  boxWidth,
  boxHeight,
}: {
  imageWidth: number;
  imageHeight: number;
  boxWidth: number;
  boxHeight: number;
}): { width: number; height: number } {
  const scale = Math.min(boxWidth / imageWidth, boxHeight / imageHeight);
  return { width: imageWidth * scale, height: imageHeight * scale };
}

export function rotatedRectAabb({
  width,
  height,
  degrees,
}: {
  width: number;
  height: number;
  degrees: number;
}): { width: number; height: number } {
  const radians = (degrees * Math.PI) / 180;
  const cos = Math.abs(Math.cos(radians));
  const sin = Math.abs(Math.sin(radians));
  return {
    width: width * cos + height * sin,
    height: width * sin + height * cos,
  };
}

export function imageOnlySlideFitsSafeArea({
  imageWidth,
  imageHeight,
  boxWidth,
  boxHeight,
  tilt,
}: {
  imageWidth: number;
  imageHeight: number;
  boxWidth: number;
  boxHeight: number;
  tilt: SlideImageTiltValue;
}): boolean {
  const contained = containImageInBox({
    imageWidth,
    imageHeight,
    boxWidth,
    boxHeight,
  });
  const fitSide =
    Math.min(contained.width, contained.height) *
    SLIDE_IMAGE_ONLY_TILT_FIT_SCALE;
  const aabb = rotatedRectAabb({
    width: fitSide,
    height: fitSide,
    degrees: tilt === 'NONE' ? 0 : SLIDE_IMAGE_ONLY_TILT_DEGREES,
  });
  const epsilon = 1e-6;
  return aabb.width <= boxWidth + epsilon && aabb.height <= boxHeight + epsilon;
}

export function slideImageTiltAnimationClass({
  tilt,
  imageOnly,
  styles,
}: {
  tilt: SlideImageTiltValue;
  imageOnly: boolean;
  styles: SlideImageTiltStyles;
}): string {
  if (tilt === 'NONE') {
    return styles.enterFlat;
  }
  if (imageOnly) {
    return tilt === 'LEFT'
      ? styles.tiltFullBleedLeft
      : styles.tiltFullBleedRight;
  }
  return tilt === 'LEFT' ? styles.tiltImageLeft : styles.tiltImageRight;
}

export function slideContentTiltAnimationClass({
  tilt,
  styles,
}: {
  tilt: SlideImageTiltValue;
  styles: SlideImageTiltStyles;
}): string {
  if (tilt === 'NONE') {
    return styles.enterFlat;
  }
  return tilt === 'LEFT' ? styles.tiltContentRight : styles.tiltContentLeft;
}
